import { DebounceEmitter } from "../../../base/common/events.js";
import { Disposable } from "../../../base/common/lifecycle.js";
import { ILogService } from "../../log/common/log.js";
import { IResourceEntry, IResourceService, ResourceKind, ResourceKindCounts, ResourceOrigin, ResourcesChangedEvent, ResourceUsage } from "./resources.js";


interface IResourceRef {
    deref(): object | undefined;
}

interface IFinalizationRegistryLike {
    register(target: object, heldValue: number, unregisterToken?: object): void;
}

class ResourceEntry implements IResourceEntry {

    alive = true;
    pinned = false;
    name: string;
    type: string;
    /** primitive snapshot (source-file bytes) — safe after GC */
    byteSize?: number;

    constructor(
        readonly id: number,
        readonly kind: ResourceKind,
        readonly uuid: string | undefined,
        name: string,
        type: string,
        private readonly _ref: IResourceRef,
    ) {
        this.name = name;
        this.type = type;
    }

    deref(): object | undefined {
        return this.alive ? this._ref.deref() : undefined;
    }
}

/**
 * Iterable weak collection of scene resources.
 *
 * Correctness requirements (the editor is injected into third-party pages — we must
 * never retain host objects):
 * - map keys are primitive ids, never the resource itself
 * - the WeakRef observes the REAL resource
 * - the FinalizationRegistry heldValue is a primitive id (heldValues are held strongly);
 *   the unregister token (held weakly per spec) is the resource itself
 * - the only strong references are user pins in `_pinned`
 */
export class ResourceService extends Disposable implements IResourceService {

    readonly _serviceBrand: undefined;

    private _lastId = 0;

    private readonly _entriesById = new Map<number, ResourceEntry>();
    private readonly _idByResource = new WeakMap<object, number>();
    /** The ONLY strong references held by this service. */
    private readonly _pinned = new Map<number, object>();
    private readonly _collectedByKind = new Map<ResourceKind, number>();
    /** resource id → { model id, glTF pointer } it was loaded from (recordOrigin). */
    private readonly _originByResourceId = new Map<number, { modelId: number; pointer?: string }>();
    private readonly _modelSources = new Map<number, { root: IResourceRef; uri: string }>();

    private readonly _finalization: IFinalizationRegistryLike;

    private readonly _onDidChangeResources = this._register(new DebounceEmitter<ResourcesChangedEvent>({
        merge: (events) => ({
            added: events.flatMap(e => e.added),
            collected: events.flatMap(e => e.collected),
            pinChanged: events.flatMap(e => e.pinChanged),
            updated: events.flatMap(e => e.updated),
        }),
        delay: 100,
    }));
    readonly onDidChangeResources = this._onDidChangeResources.event;

    constructor(
        @ILogService private readonly logService: ILogService,
    ) {
        super();
        this._finalization = this._createFinalizationRegistry(id => this._onCollected(id));
    }

    /** Overridable for tests (real GC is not deterministically testable). */
    protected _createRef(resource: object): IResourceRef {
        return new WeakRef(resource);
    }

    /** Overridable for tests. */
    protected _createFinalizationRegistry(onCollected: (id: number) => void): IFinalizationRegistryLike {
        return new FinalizationRegistry<number>(onCollected);
    }

    register(resource: object, kind: ResourceKind, options?: { label?: string, type?: string, byteSize?: number }): void {
        if (!resource || typeof resource !== "object") return;

        const existingId = this._idByResource.get(resource);
        if (existingId !== undefined) {
            const existing = this._entriesById.get(existingId);
            if (existing) {
                // Known + live: refresh the snapshot so periodic sweeps keep names current.
                const name = this._snapshotName(resource) || options?.label || "";
                const type = this._snapshotType(resource, options?.type);
                const byteSize = options?.byteSize || existing.byteSize;
                if (existing.name !== name || existing.type !== type || existing.byteSize !== byteSize) {
                    existing.name = name;
                    existing.type = type;
                    existing.byteSize = byteSize;
                    this._fire({ updated: [existing] });
                }
                return;
            }
        }

        const id = ++this._lastId;
        const uuid = (resource as { uuid?: unknown }).uuid;
        const entry = new ResourceEntry(
            id,
            kind,
            typeof uuid === "string" ? uuid : undefined,
            this._snapshotName(resource) || options?.label || "",
            this._snapshotType(resource, options?.type),
            this._createRef(resource),
        );
        if (options?.byteSize) entry.byteSize = options.byteSize;
        this._entriesById.set(id, entry);
        this._idByResource.set(resource, id);
        this._finalization.register(resource, id, resource);
        this._fire({ added: [entry] });
    }

    has(resource: object): boolean {
        const id = this._idByResource.get(resource);
        return id !== undefined && this._entriesById.has(id);
    }

    getEntry(resource: object): IResourceEntry | undefined {
        const id = this._idByResource.get(resource);
        if (id === undefined) return undefined;
        const entry = this._entriesById.get(id);
        return entry && entry.deref() !== undefined ? entry : undefined;
    }

    *entries(kind?: ResourceKind): Iterable<IResourceEntry> {
        for (const entry of [...this._entriesById.values()]) {
            if (kind !== undefined && entry.kind !== kind) continue;
            // Prune entries whose resource died before the finalizer ran.
            if (entry.deref() === undefined) {
                this._onCollected(entry.id);
                continue;
            }
            yield entry;
        }
    }

    *resources(kind?: ResourceKind): Iterable<{ entry: IResourceEntry; resource: object }> {
        for (const entry of this.entries(kind)) {
            const resource = entry.deref();
            if (resource) {
                yield { entry, resource };
            }
        }
    }

    counts(): ReadonlyMap<ResourceKind, ResourceKindCounts> {
        const result = new Map<ResourceKind, { alive: number; pinned: number; collected: number }>();
        const get = (kind: ResourceKind) => {
            let c = result.get(kind);
            if (!c) {
                c = { alive: 0, pinned: 0, collected: 0 };
                result.set(kind, c);
            }
            return c;
        };
        for (const entry of this.entries()) {
            const c = get(entry.kind);
            c.alive++;
            if (entry.pinned) c.pinned++;
        }
        for (const [kind, collected] of this._collectedByKind) {
            get(kind).collected = collected;
        }
        return result;
    }

    pin(target: object | IResourceEntry): boolean {
        const resolved = this._resolve(target);
        if (!resolved) return false;
        const { entry, resource } = resolved;
        if (!resource) return false;
        if (entry.pinned) return true;
        this._pinned.set(entry.id, resource);
        entry.pinned = true;
        this._fire({ pinChanged: [entry] });
        return true;
    }

    unpin(target: object | IResourceEntry): boolean {
        const resolved = this._resolve(target);
        if (!resolved || !resolved.entry.pinned) return false;
        this._pinned.delete(resolved.entry.id);
        resolved.entry.pinned = false;
        this._fire({ pinChanged: [resolved.entry] });
        return true;
    }

    isPinned(target: object | IResourceEntry): boolean {
        return this._resolve(target)?.entry.pinned === true;
    }

    // #region usage tracking — primitive snapshots only (see resources.ts).
    // Edges are tagged with a pass counter; completing a pass prunes edges the
    // sweep did not re-see, so removed consumers age out within one sweep cycle
    // while event-driven mid-pass records always survive the next prune.

    private _usagePass = 0;
    private _usagesCompletedOnce = false;
    /** entry id → consumer uuid → snapshot + last-seen pass (all primitives). */
    private readonly _usagesById = new Map<number, Map<string, { usage: ResourceUsage; pass: number }>>();

    get usagesReady(): boolean {
        return this._usagesCompletedOnce;
    }

    recordUsage(resource: object, consumer: ResourceUsage, slot?: string): void {
        if (!resource || typeof resource !== "object") return;
        if (!consumer || typeof consumer.uuid !== "string" || consumer.uuid.length === 0) return;
        const id = this._idByResource.get(resource);
        if (id === undefined || !this._entriesById.has(id)) return;
        let usages = this._usagesById.get(id);
        if (!usages) this._usagesById.set(id, (usages = new Map()));
        const existing = usages.get(consumer.uuid);
        if (existing) {
            // Slots accumulate WITHIN a pass (a texture bound to two slots on one
            // material) but reset ACROSS passes (stale bindings age out) — keyed
            // off whether this is the first record of the consumer this pass.
            const samePass = existing.pass === this._usagePass;
            existing.pass = this._usagePass;
            const prev = samePass ? (existing.usage.slots ?? []) : [];
            const slots = slot && !prev.includes(slot) ? [...prev, slot] : prev;
            existing.usage = slots.length ? { ...consumer, slots } : { ...consumer };
            return;
        }
        usages.set(consumer.uuid, { usage: slot ? { ...consumer, slots: [slot] } : consumer, pass: this._usagePass });
        const entry = this._entriesById.get(id);
        if (entry) this._fire({ updated: [entry] });
    }

    completeUsagePass(): void {
        const changed: ResourceEntry[] = [];
        for (const [id, usages] of [...this._usagesById]) {
            let pruned = false;
            for (const [uuid, record] of [...usages]) {
                if (record.pass < this._usagePass) {
                    usages.delete(uuid);
                    pruned = true;
                }
            }
            if (usages.size === 0) this._usagesById.delete(id);
            if (pruned) {
                const entry = this._entriesById.get(id);
                if (entry) changed.push(entry);
            }
        }
        this._usagePass++;
        const firstPass = !this._usagesCompletedOnce;
        this._usagesCompletedOnce = true;
        // the first completed pass flips "dangling" from unknowable to known —
        // notify even without pruning so UIs re-evaluate
        if (changed.length > 0 || firstPass) this._fire({ updated: changed });
    }

    getUsages(target: object | IResourceEntry): readonly ResourceUsage[] {
        const resolved = this._resolve(target);
        if (!resolved) return [];
        const usages = this._usagesById.get(resolved.entry.id);
        if (!usages || usages.size === 0) return [];
        return [...usages.values()].map(record => record.usage);
    }

    recordOrigin(resource: object, model: object, pointer?: string): void {
        const res = this._resolve(resource);
        const mdl = this._resolve(model);
        if (!res || !mdl) return; // both must be registered
        this._originByResourceId.set(res.entry.id, { modelId: mdl.entry.id, pointer });
    }

    recordModelSource(model: object, root: object, uri: string): void {
        const entry = this.getEntry(model);
        if (!entry || entry.kind !== "model") throw new Error("Model source requires a registered model.");
        const previous = this._modelSources.get(entry.id);
        if (previous?.uri === uri && previous.root.deref() === root) return;
        this._modelSources.set(entry.id, { root: this._createRef(root), uri });
        this._fire({ updated: [entry] });
    }

    *modelSources(): Iterable<{ model: IResourceEntry; root: object; uri: string }> {
        for (const [id, source] of this._modelSources) {
            const model = this._entriesById.get(id);
            const root = source.root.deref();
            if (!model?.deref() || !root) { this._modelSources.delete(id); continue; }
            yield { model, root, uri: source.uri };
        }
    }

    getOrigin(target: object | IResourceEntry): ResourceOrigin | undefined {
        const resolved = this._resolve(target);
        if (!resolved) return undefined;
        const origin = this._originByResourceId.get(resolved.entry.id);
        if (!origin) return undefined;
        // dropped if the model was collected (stale origin → treat as none)
        const model = this._entriesById.get(origin.modelId);
        if (!model) { this._originByResourceId.delete(resolved.entry.id); return undefined; }
        return { model, pointer: origin.pointer };
    }

    // #endregion

    private _resolve(target: object | IResourceEntry): { entry: ResourceEntry; resource: object | undefined } | null {
        if (target instanceof ResourceEntry) {
            const entry = this._entriesById.get(target.id);
            return entry ? { entry, resource: entry.deref() } : null;
        }
        const id = this._idByResource.get(target);
        if (id === undefined) return null;
        const entry = this._entriesById.get(id);
        return entry ? { entry, resource: target } : null;
    }

    private _onCollected(id: number): void {
        const entry = this._entriesById.get(id);
        if (!entry) return; // already pruned during iteration; stale finalizer callback
        this.logService.trace(`Resource has been garbage collected: ${entry.kind} "${entry.name}"`);
        entry.alive = false;
        entry.pinned = false;
        this._pinned.delete(id); // defensive — a pinned resource cannot be collected
        this._entriesById.delete(id);
        this._modelSources.delete(id);
        this._usagesById.delete(id);
        this._originByResourceId.delete(id); // as a resource; stale model refs are pruned lazily in getOrigin
        this._collectedByKind.set(entry.kind, (this._collectedByKind.get(entry.kind) ?? 0) + 1);
        this._fire({ collected: [entry] });
    }

    private _fire(event: Partial<ResourcesChangedEvent>): void {
        this._onDidChangeResources.fire({
            added: event.added ?? [],
            collected: event.collected ?? [],
            pinChanged: event.pinChanged ?? [],
            updated: event.updated ?? [],
        });
    }

    private _snapshotName(resource: object): string {
        const name = (resource as { name?: unknown }).name;
        return typeof name === "string" ? name : "";
    }

    private _snapshotType(resource: object, fallback?: string): string {
        const type = (resource as { type?: unknown }).type;
        if (typeof type === "string" && type.length > 0) return type;
        // caller-provided fallback beats the constructor name — which is
        // minified on host pages, or a useless "Object" for plain wrappers
        if (fallback) return fallback;
        return resource.constructor?.name ?? "Object";
    }
}
